import { useState, useEffect } from "react";
import FormSection from "../ui/FormSection";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import StudyController from "@/network/controllers/study.controller";
import NotificationController from "@/network/controllers/notification.controller";

export default function StudySettings({
  study = {},
  settings = {},
  onUpdateStudy,
  onCloseStudy,
  isClosed: isClosedProp,
}) {
  const [localData, setLocalData] = useState({
    name: "",
    duration: "",
    threshold: "",
    masterICFName: "",
  });
  const [masterICFs, setMasterICFs] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCloseStudyModal, setShowCloseStudyModal] = useState(false);
  const [showCloseSuccessModal, setShowCloseSuccessModal] = useState(false);
  const [showDeleteICFErrorModal, setShowDeleteICFErrorModal] = useState(false);
  const [deleteICFErrorMessage, setDeleteICFErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isClosed, setIsClosed] = useState(
    isClosedProp ?? (study?.status === "closed")
  );
  const [showDeleteStudyModal, setShowDeleteStudyModal] = useState(false);

  // Update local state when study or settings prop changes
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0 && settings.study) {
      console.log("[StudySettings] settings payload", settings);
      const newData = {
        name: settings.study.title || "",
        duration:
        settings.study.estimated_participant_participation_duration ??
          settings.study.estimated_duration ??
         
          "",
        threshold: settings.study.threshold_of_consenting_participants || "",
        masterICFName: "",
      };
      console.log("[StudySettings] normalised data", newData);
      setLocalData(newData);
      setOriginalData(newData);

      const mappedMasterICFs = (settings.master_icfs || []).map((icf) => ({
        ...icf,
        name: icf.master_icf_name,
      }));
      setMasterICFs(mappedMasterICFs);

      setActiveVersion(
        mappedMasterICFs?.find((icf) => icf.isActive)?.id ||
        (mappedMasterICFs?.length > 0
          ? mappedMasterICFs[mappedMasterICFs.length - 1].id
          : null)
      );

      if (isClosedProp === undefined) {
        setIsClosed(settings.study.status === "closed");
      }
    } else if (study && Object.keys(study).length > 0) {
      console.log("[StudySettings] fallback study payload", study);
      const newData = {
        name: study.title || "",
        duration: "",
        threshold: "",
        masterICFName: "",
      };
      setLocalData(newData);
      setOriginalData(newData);
      setMasterICFs([]);
      setActiveVersion(null);
      // Do not override local isClosed here; wait for settings or explicit close
    }
  }, [study, settings, isClosedProp]);

  const handleDeleteStudyConfirm = () => {
    setShowDeleteStudyModal(false);
    console.log("Study deleted");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`[StudySettings] input change -> ${name}:`, value);
    setLocalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    console.log("[StudySettings] localData snapshot", localData);
  }, [localData]);

  // Se il padre fornisce isClosed, sincronizza lo stato locale
  useEffect(() => {
    if (isClosedProp !== undefined) {
      setIsClosed(!!isClosedProp);
    }
  }, [isClosedProp]);

  //TODO: METTRE A JOUR AUSSI LA BLOCKCHAIN
  const handleAddMasterICF = async () => {
    if (isClosed) return;
    const trimmedName = localData.masterICFName.trim();
    if (!trimmedName || !study?.id) return;

    try {
      const response = await StudyController.addMasterIcfToStudy(
        study.id,
        trimmedName
      );

      if (response && response.id) {
        const newMasterICF = {
          ...response,
          name: response.master_icf_name || response.title || trimmedName,
          isActive: masterICFs.length === 0,
        };

        const updatedMasterICFs = [...masterICFs, newMasterICF];
        setMasterICFs(updatedMasterICFs);
        setLocalData((prev) => ({ ...prev, masterICFName: "" }));

        if (masterICFs.length === 0) {
          setActiveVersion(newMasterICF.id);
        }

        if (onUpdateStudy) {
          onUpdateStudy();
        }

        console.log(" Master ICF added successfully");
      } else {
        console.warn("⚠️ Backend did not return a valid ICF object:", response);
      }
    } catch (error) {
      console.error("Error while adding Master ICF:", error);
    }
  };

  const handleSetActiveVersion = (id) => {
    if (isClosed) return;
    const updatedMasterICFs = masterICFs.map((icf) => ({
      ...icf,
      isActive: icf.id === id,
    }));
    setMasterICFs(updatedMasterICFs);
    setActiveVersion(id);

    // Update the parent component
    if (onUpdateStudy) {
      onUpdateStudy({
        ...study,
        masterICF: updatedMasterICFs,
      });
    }
  };

  //TODO: METTRE A JOUR AUSSI LA BLOCKCHAIN
  const handleDeleteVersion = async (id) => {
    if (isClosed) return;
    if (masterICFs.length <= 1) {
      alert("At least one Master ICF must remain.");
      return;
    }

    try {
      const response = await StudyController.deactivateMasterIcfFromStudy(
        study.id,
        id
      );
      if (response.success) {
        const updatedMasterICFs = masterICFs.filter((icf) => icf.id !== id);

        if (id === activeVersion && updatedMasterICFs.length > 0) {
          const lastICF = updatedMasterICFs[updatedMasterICFs.length - 1];
          lastICF.isActive = true;
          setActiveVersion(lastICF.id);
        }

        setMasterICFs(updatedMasterICFs);

        if (onUpdateStudy) {
          onUpdateStudy({
            ...study,
            masterICF: updatedMasterICFs,
          });
        }
      } else {
        setDeleteICFErrorMessage(
          response.message ||
          "This Master ICF version cannot be deleted because it is used by participants."
        );
        setShowDeleteICFErrorModal(true);
      }
    } catch (error) {
      console.error("Error while deleting Master ICF:", error);
    }
  };

  const handleEditClick = () => {
    if (isClosed) return;
    setOriginalData({ ...localData });
    setEditMode(true);
  };

  const handleSaveClick = () => {
    if (isClosed) return;
    setShowSaveModal(true);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  //TODO: METTRE A JOUR AUSSI LA BLOCKCHAIN
  const handleCloseStudyConfirm = async () => {
    setShowCloseStudyModal(false);

    try {
      const response = await StudyController.closeStudyById(study.id);

      if (response?.data?.status === "closed") {
        console.log("✅ Study closed successfully");
        setIsClosed(true);
        setShowCloseSuccessModal(true);
        if (onCloseStudy) onCloseStudy();
      } else {
        console.warn("⚠️ Backend did not confirm study closure:", response);
      }
    } catch (error) {
      console.error("❌ Error closing the study:", error);
    }
  };

  //TODO: METTRE A JOUR AUSSI LA BLOCKCHAIN
  const handleSaveConfirm = async () => {
    setEditMode(false);
    setShowSaveModal(false);

    try {
      const payload = {
        studyId: study.id,
        title: localData.name,
        estimated_duration: localData.duration,
        threshold_of_consenting_participants: localData.threshold,
      };
      console.log("[StudySettings] save payload", payload);
      const response = await StudyController.updateStudyGeneralSettingsCRO(payload);
      console.log("[StudySettings] save response", response);

      if (response.success) {
        console.log(" Study settings updated successfully");
        if (onUpdateStudy) {
          onUpdateStudy({
            ...study,
            title: localData.name,
            estimated_duration: localData.duration,
            estimated_participant_participation_duration: localData.duration,
            threshold_of_consenting_participants: localData.threshold,
          });
        }
      } else {
        console.error(" Failed to update study settings:", response.message);
      }
    } catch (err) {
      console.error(" Exception while updating study settings:", err.message);
    }
  };

  const handleCancelConfirm = () => {
    setLocalData(originalData);
    setEditMode(false);
    setShowCancelModal(false);
  };

  // Icon styles for the square background design - similar to the table components
  const iconContainerStyle =
    "flex items-center justify-center w-6 h-6 bg-gray-800 text-white rounded cursor-pointer";

  // Custom input style with text truncation
  const getInputStyle = (isEditMode) => {
    return isEditMode
      ? "bg-white h-10 text-sm truncate"
      : "bg-gray-200 h-10 text-sm truncate";
  };

  return (
    <div className="bg-dashboard-bg1 p-6 rounded-xl relative">
      <div className={`${isClosed ? "pointer-events-none opacity-60" : ""}`}>
        {/* Study Settings Section */}
        <FormSection title="Study settings">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <div className="h-6 mb-1 relative group">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 truncate whitespace-nowrap overflow-hidden"
                >
                  Name
                </label>
                <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 bg-gray-800 text-white px-2 py-1 text-xs rounded z-10 whitespace-normal transition-opacity duration-200 pointer-events-none">
                  Name
                </div>
              </div>
              <input
                id="name"
                name="name"
                type="text"
                value={localData.name}
                onChange={handleInputChange}
                placeholder="Study name"
                disabled={!editMode}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${getInputStyle(
                  editMode
                )}`}
                title={localData.name}
              />
            </div>

            <div className="md:col-span-4">
              <div className="h-6 mb-1 relative group">
                <label
                  htmlFor="duration"
                  className="block text-sm font-medium text-gray-700 truncate whitespace-nowrap overflow-hidden"
                >
                  Estimated patient participation duration
                </label>
                <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 bg-gray-800 text-white px-2 py-1 text-xs rounded z-10 whitespace-normal transition-opacity duration-200 pointer-events-none">
                  Estimated patient participation duration
                </div>
              </div>
              <input
                id="duration"
                name="duration"
                type="text"
                value={localData.duration}
                onChange={handleInputChange}
                placeholder="In days, months, years, ..."
                disabled={!editMode}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${getInputStyle(
                  editMode
                )}`}
                title={localData.duration}
              />
            </div>

            <div className="md:col-span-3 relative">
              <div className="h-6 mb-1 relative group">
                <label
                  htmlFor="threshold"
                  className="block text-sm font-medium text-gray-700 truncate whitespace-nowrap overflow-hidden"
                >
                  Threshold of consenting participants
                </label>
                <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 bg-gray-800 text-white px-2 py-1 text-xs rounded z-10 whitespace-normal transition-opacity duration-200 pointer-events-none">
                  Threshold of consenting participants
                </div>
              </div>
              <input
                id="threshold"
                name="threshold"
                type="text"
                value={localData.threshold}
                onChange={handleInputChange}
                placeholder="In number or text (e.g. 100 or '1 month')"
                disabled={!editMode}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${getInputStyle(
                  editMode
                )}`}
                title={localData.threshold}
              />

            </div>

            <div className="md:col-span-1 flex items-end justify-start mb-2">
              {!editMode ? (
                <div className="flex">
                  <button
                    onClick={handleEditClick}
                    className="text-gray-700 hover:text-gray-900"
                    title="Edit study settings"
                  >
                    <img
                      className="w-6 h-6"
                      alt="modifie"
                      src="/icons/study/Modifie.svg"
                    ></img>
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <div onClick={handleCancelClick} title="Cancel">
                    <img
                      className="w-6 h-6"
                      alt="cancel"
                      src="/icons/study/Cancel.svg"
                    ></img>
                  </div>
                  <div onClick={handleSaveClick} title="Save">
                    <img
                      className="w-6 h-6"
                      alt="save"
                      src="/icons/study/Save.svg"
                    ></img>
                  </div>
                </div>
              )}
            </div>
          </div>
        </FormSection>

        {/* Master ICF Section */}
        <FormSection title="Master ICF" subtitle="Add new version">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-1/3">
              <div className="h-6 mb-1 relative group">
                <label
                  htmlFor="masterICFName"
                  className="block text-sm font-medium text-gray-700 truncate whitespace-nowrap overflow-hidden"
                >
                  Master ICF version name
                </label>
                <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 bg-gray-800 text-white px-2 py-1 text-xs rounded z-10 whitespace-normal transition-opacity duration-200 pointer-events-none">
                  Master ICF version name
                </div>
              </div>
              <input
                id="masterICFName"
                name="masterICFName"
                type="text"
                value={localData.masterICFName}
                onChange={handleInputChange}
                placeholder="Master ICF version name"
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 h-10 text-sm truncate`}
                title={localData.masterICFName}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleAddMasterICF}
                variant="addbutton"
                className="mt-8"
                disabled={isLoading}
              >
                {isLoading ? "Adding..." : "Add Master ICF"}
              </Button>
            </div>
          </div>

          {masterICFs.length > 0 && (
            <div>
              <h4 className="text-md font-medium mb-3">Registered version</h4>
              <div className="bg-[#5B93FF]/20 p-6 rounded-2xl">
                <div className="flex flex-wrap gap-2">
                  {masterICFs.map((icf) => (
                    <div
                      key={icf.id}
                      className={`flex items-center ${icf.id === activeVersion
                          ? "bg-[#5B93FF]/70"
                          : "bg-[#5B93FF]/80"
                        } text-white px-3 py-3 rounded-xl text-sm`}
                      title={icf.name}
                      onClick={() =>
                        icf.id !== activeVersion &&
                        handleSetActiveVersion(icf.id)
                      }
                      style={{
                        cursor:
                          icf.id !== activeVersion ? "pointer" : "default",
                      }}
                    >
                      <span className="truncate max-w-[150px]">{icf.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVersion(icf.id);
                        }}
                        className="ml-2 text-white hover:text-gray-200 flex-shrink-0"
                        title="Delete version"
                      >
                        <span className="sr-only">Delete</span>
                        <img
                          className="w-4 h-4"
                          alt="bin"
                          src="/icons/study/Bin.svg"
                        ></img>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </FormSection>
      </div>

      <div className="flex justify-end mt-8">
        <Button
          onClick={() =>
            isClosed
              ? setShowDeleteStudyModal(true)
              : setShowCloseStudyModal(true)
          }
          variant="closestudy"
          className="hover:bg-red-700 pointer-events-auto"
        >
          {isClosed ? "Delete study" : "Close study"}
        </Button>
      </div>

      <Modal
        isOpen={showDeleteICFErrorModal}
        onClose={() => setShowDeleteICFErrorModal(false)}
        title="Cannot Delete Master ICF"
        primaryAction={() => setShowDeleteICFErrorModal(false)}
        primaryActionLabel="Close"
      >
        <p className="mb-4">{deleteICFErrorMessage}</p>
      </Modal>

      <Modal
        isOpen={showDeleteStudyModal}
        onClose={() => setShowDeleteStudyModal(false)}
        title="Delete Study"
        primaryAction={handleDeleteStudyConfirm}
        primaryButtonVariant="closestudy2"
        primaryActionLabel="Confirm deletion"
        secondaryAction={() => setShowDeleteStudyModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          Are you sure you want to delete this clinical study?
        </p>
        <p className="mb-4">
          This action is final and will remove all data associated with the
          study.
        </p>
        <p className="mb-4">Once deleted, the study cannot be recovered.</p>
      </Modal>

      {/* Close Success Modal */}
      <Modal
        isOpen={showCloseSuccessModal}
        onClose={() => setShowCloseSuccessModal(false)}
        title="Study successfully closed"
        primaryAction={() => setShowCloseSuccessModal(false)}
        primaryActionLabel="OK"
      >
        <p className="mb-4">The study status has been updated to closed. All forms are now read‑only and no further changes can be made.</p>
      </Modal>

      {/* Save Confirmation Modal */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Save changes"
        primaryAction={handleSaveConfirm}
        primaryActionLabel="Save Changes"
        secondaryAction={() => setShowSaveModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          You are about to save the modifications made to the study.
        </p>
        <p className="font-medium">
          Once saved, these modifications will be applied and may affect the
          workflow of investigators and participants.
        </p>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Discard changes"
        primaryAction={handleCancelConfirm}
        primaryActionLabel="Discard Changes"
        secondaryAction={() => setShowCancelModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-8">
          You are about to discard all modifications made to the study.
        </p>
        <p className="font-medium">
          The study will remain as it was before editing.
        </p>
      </Modal>

      {/* Delete Study Confirmation Modal */}
      <Modal
        isOpen={showCloseStudyModal}
        onClose={() => setShowCloseStudyModal(false)}
        title="Study closure"
        primaryAction={handleCloseStudyConfirm}
        primaryButtonVariant="closestudy2"
        primaryActionLabel="Confirm closure"
        secondaryAction={() => setShowCloseStudyModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          Are you sure you want to close this clinical study?
        </p>
        <p className="mb-4">
          This action is final and will prevent any further modifications or new
          data entries. All existing data will remain securely stored and
          accessible in read-only mode.
        </p>
        <p className="mb-4">Once closed, the study cannot be reopened.</p>
      </Modal>
    </div>
  );
}
